'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';

// UI lib components
import { Stack, Group, Text, Loader, ActionIcon, Tooltip } from '@mantine/core';

// UI local components
import TextInput from '@ui-kit/molecules/TextInput';

// Types
import { ResourceInput } from './types';

// Icons
import { IconRefresh } from '@tabler/icons-react';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

interface ResultsViewProps {
  resources: ResourceInput[];
  suggestedNames: Record<string, string>;
  loadingResources: Set<string>;
  onNameChange: (resourceId: string, value: string) => void;
  onRegenerateSuggestion: (resourceId: string) => void;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export default function ResultsView({
  resources,
  suggestedNames,
  loadingResources,
  onNameChange,
  onRegenerateSuggestion,
}: ResultsViewProps) {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: resources.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72,
    overscan: 5,
  });

  return (
    <Stack gap="0" style={{ flex: 1, minHeight: 0 }}>
      {/* Column headers - sticky */}
      <Group
        gap="2rem"
        wrap="nowrap"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'var(--mantine-color-body)',
          paddingBottom: '1rem',
        }}
      >
        <Text size="sm" fw={500} style={{ flex: 1 }}>
          {t('Resource:DialogResourceEdit.fields.oldName.label')}
        </Text>
        <Text size="sm" fw={500} style={{ flex: 1 }}>
          {t('Resource:DialogResourceEdit.fields.newName.label')}
        </Text>
      </Group>
      {/* Single scroll container for both columns */}
      <div
        ref={parentRef}
        style={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const resource = resources[virtualRow.index];
            const isLoading = loadingResources.has(resource.id);
            const suggestedName = suggestedNames[resource.id];

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                  paddingBottom: '1rem',
                }}
              >
                <Group gap="lg" wrap="nowrap" align="flex-end">
                  {/* Old name column */}
                  <TextInput
                    value={resource.name}
                    disabled
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  {/* New name column */}
                  <Group
                    gap="0.5rem"
                    wrap="nowrap"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <TextInput
                      value={suggestedName}
                      onChange={(event) =>
                        onNameChange(resource.id, event.currentTarget.value)
                      }
                      placeholder={
                        isLoading
                          ? t(
                              'Resource:DialogResourceEdit.generatingSuggestion',
                            )
                          : t(
                              'Resource:DialogResourceEdit.suggestionPlaceholder',
                            )
                      }
                      disabled={isLoading}
                      style={{ flex: 1 }}
                      rightSection={
                        isLoading ? <Loader size="xs" /> : undefined
                      }
                    />
                    <Tooltip
                      label={t(
                        'Resource:DialogResourceEdit.regenerateSuggestion',
                      )}
                      position="top"
                      withArrow
                    >
                      <ActionIcon
                        variant="subtle"
                        size="md"
                        onClick={() => onRegenerateSuggestion(resource.id)}
                        disabled={isLoading}
                      >
                        <IconRefresh size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              </div>
            );
          })}
        </div>
      </div>
    </Stack>
  );
}
